// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Toggle } from '@carbon/react';
import { ChevronLeft } from '@carbon/icons-react';
import { useShellStore, type A11yPrefs } from '../../stores/shellStore';
import type { UserRole } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { usePersona } from '../../hooks/usePersona';
import { useActiveSite } from '../../hooks/useActiveSite';
import './Preferences.css';

/** Design copy (AB#34855). Static strings — the app has no i18n layer yet. */
export const PREFS_COPY = {
  title: 'Preferences',
  back: 'Back',
  note: 'Personal preferences only. Nothing here changes site operations.',
  appearance: 'Appearance',
  accessibility: 'Accessibility',
  theme: {
    label: 'Theme',
    description: 'Gray 100 dark or Gray 10 light.',
    dark: 'Dark',
    light: 'Light',
  },
  timeZone: {
    label: 'Time zone',
    description: 'Show times on the site clock or your browser clock.',
    site: 'Site time',
    browser: 'My browser time',
  },
  reduceMotion: { label: 'Reduce motion', description: 'Stop status pulses and transitions.' },
  highContrast: { label: 'High contrast borders', description: 'Stronger edges between surfaces.' },
  largeText: { label: 'Larger text', description: 'Increase base font size.' },
} as const;

const ROLE_LABEL: Record<UserRole, string> = {
  operator: 'Operator',
  operations_manager: 'Ops Manager',
  admin: 'Admin',
  executive: 'Executive',
};

const A11Y_ROWS: { key: keyof A11yPrefs; copy: { label: string; description: string } }[] = [
  { key: 'reduceMotion', copy: PREFS_COPY.reduceMotion },
  { key: 'highContrast', copy: PREFS_COPY.highContrast },
  { key: 'largeText', copy: PREFS_COPY.largeText },
];

/** A label + one-line description on the left, the control right-aligned. */
function Row({
  id,
  label,
  description,
  children,
}: {
  id: string;
  label: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="prefs-row">
      <div>
        <div className="prefs-row-label" id={`${id}-label`}>
          {label}
        </div>
        <div className="prefs-row-desc">{description}</div>
      </div>
      {children}
    </div>
  );
}

/** Preferences — appearance (theme) + accessibility, persisted in shellStore. */
export default function Preferences() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { effectiveRole } = usePersona();
  const { siteLabel, isFleet } = useActiveSite();
  const theme = useShellStore(s => s.theme);
  const setTheme = useShellStore(s => s.setTheme);
  const a11y = useShellStore(s => s.a11y);
  const setA11y = useShellStore(s => s.setA11y);
  const timeZonePolicy = useShellStore(s => s.timeZonePolicy);
  const setTimeZonePolicy = useShellStore(s => s.setTimeZonePolicy);

  // /me can return empty names; fall back like HeliosHeader so the avatar is never blank.
  const name =
    `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim() || user?.email || 'Operator';
  const initials = name
    .split(/\s+/, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();
  // Executives have no site in the header ("All regions"); managers may be on the whole fleet.
  const scope = effectiveRole === 'executive' ? 'All regions' : isFleet ? 'Fleet' : siteLabel;
  const subline = [effectiveRole && ROLE_LABEL[effectiveRole], scope].filter(Boolean).join(' · ');

  // BrowserRouter keeps its entry index in history.state; 0 means we landed here directly.
  const goBack = () => {
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate('/', { replace: true });
  };

  return (
    <div className="hs-page prefs-page">
      <h1 className="cds--visually-hidden">{PREFS_COPY.title}</h1>
      <button type="button" className="prefs-back" onClick={goBack}>
        <ChevronLeft size={16} /> {PREFS_COPY.back}
      </button>

      <header className="prefs-profile">
        <span className="prefs-avatar" aria-hidden>
          {initials}
        </span>
        <div>
          <div className="prefs-name">{name}</div>
          {subline && <div className="prefs-subline">{subline}</div>}
        </div>
      </header>
      <p className="prefs-note">{PREFS_COPY.note}</p>

      <section className="prefs-group" aria-labelledby="prefs-appearance">
        <h2 className="prefs-group-title" id="prefs-appearance">
          {PREFS_COPY.appearance}
        </h2>
        <Row id="pref-theme" {...PREFS_COPY.theme}>
          <div className="hs-segmented prefs-seg" role="group" aria-labelledby="pref-theme-label">
            <button
              type="button"
              className={`hs-pill${theme === 'g100' ? ' active' : ''}`}
              aria-pressed={theme === 'g100'}
              onClick={() => setTheme('g100')}
            >
              {PREFS_COPY.theme.dark}
            </button>
            <button
              type="button"
              className={`hs-pill${theme === 'g10' ? ' active' : ''}`}
              aria-pressed={theme === 'g10'}
              onClick={() => setTheme('g10')}
            >
              {PREFS_COPY.theme.light}
            </button>
          </div>
        </Row>
        <Row id="pref-time-zone" {...PREFS_COPY.timeZone}>
          <div
            className="hs-segmented prefs-seg"
            role="group"
            aria-labelledby="pref-time-zone-label"
          >
            <button
              type="button"
              className={`hs-pill${timeZonePolicy === 'site' ? ' active' : ''}`}
              aria-pressed={timeZonePolicy === 'site'}
              onClick={() => setTimeZonePolicy('site')}
            >
              {PREFS_COPY.timeZone.site}
            </button>
            <button
              type="button"
              className={`hs-pill${timeZonePolicy === 'browser' ? ' active' : ''}`}
              aria-pressed={timeZonePolicy === 'browser'}
              onClick={() => setTimeZonePolicy('browser')}
            >
              {PREFS_COPY.timeZone.browser}
            </button>
          </div>
        </Row>
      </section>

      <section className="prefs-group" aria-labelledby="prefs-accessibility">
        <h2 className="prefs-group-title" id="prefs-accessibility">
          {PREFS_COPY.accessibility}
        </h2>
        {A11Y_ROWS.map(({ key, copy }) => (
          <Row key={key} id={`pref-${key}`} {...copy}>
            <Toggle
              id={`pref-${key}`}
              labelText={copy.label}
              hideLabel
              labelA=""
              labelB=""
              toggled={a11y[key]}
              onToggle={v => setA11y({ [key]: v })}
            />
          </Row>
        ))}
      </section>
    </div>
  );
}
